<template>

  <div
    class="kmselect"
    :style="appearanceOverrides"
  >
    <div
      ref="containerEl"
      class="kmselect-field"
      @click="onContainerClick"
    >
      <div
        class="kmselect-container"
        :class="[
          {
            'has-label': label,
            'is-disabled': disabled,
            'is-active': inputFocused || isPopupOpen,
            'is-invalid': invalid,
          },
          $computedClass({
            ':hover:not(.is-disabled):not(.is-active):not(.is-invalid)': {
              borderBottomColor: 'var(--tokens-text)',
            },
          }),
        ]"
        :style="{
          backgroundColor: disabled ? $themeTokens.textDisabled + '20' : 'var(--palette-grey-v100)',
          borderRadius: '2px 2px 0 0',
          borderBottom: `1px solid ${
            invalid
              ? 'var(--tokens-error)'
              : inputFocused
                ? 'var(--tokens-primary)'
                : 'var(--palette-grey-v700)'
          }`,
        }"
        @mouseenter="isHovered = true"
        @mouseleave="isHovered = false"
      >
        <label
          v-if="label"
          :id="labelId"
          :for="inputId"
          class="kmselect-label"
          :class="{
            'is-inline': isLabelInline,
            'is-floating': !isLabelInline,
          }"
          :style="{
            color: disabled
              ? 'var(--tokens-textDisabled)'
              : invalid
                ? 'var(--tokens-error)'
                : inputFocused || isPopupOpen
                  ? 'var(--tokens-primary)'
                  : isHovered
                    ? 'var(--tokens-text)'
                    : 'var(--palette-grey-v700)',
          }"
        >
          {{ label }}
        </label>
        <div
          v-if="multiple"
          :id="selectedValuesId"
          class="visuallyhidden"
          aria-hidden="true"
        >
          {{ selectedValuesSummary }}
        </div>
        <KMultiSelectInput
          ref="inputComponent"
          :selectedOptions="selectedOptionsData"
          :searchText="internalSearchText"
          :placeholder="computedPlaceholder"
          :listboxId="listboxId"
          :errorId="errorId"
          :isOpen="isOpen"
          :expanded="expanded"
          :multiple="multiple"
          :itemText="itemText"
          :itemValue="itemValue"
          :clearable="clearable"
          :clearAllLabel="typeof messages.clearText === 'function' ? messages.clearText() : ''"
          :openLabel="typeof messages.open === 'function' ? messages.open() : ''"
          :closeLabel="typeof messages.close === 'function' ? messages.close() : ''"
          :disabled="disabled"
          :invalid="invalid"
          :focused="inputFocused"
          :hovered="isHovered && !inputFocused && !isPopupOpen"
          :activeDescendant="activeDescendantId"
          :labelId="labelId"
          :inputId="inputId"
          :selectedValuesId="selectedValuesId"
          @update:searchText="onSearchInput"
          @input-keydown="onInputKeydown"
          @input-focus="onInputFocus"
          @input-blur="onInputBlur"
          @chip-remove="onChipRemove"
          @clear-all="onClearAll"
          @toggle="onToggleDropdown"
        >
          <template
            v-if="$scopedSlots.chip"
            #chip="slotProps"
          >
            <!-- @slot Custom slot for rendering selected items (chips) in multiple select mode. -->
            <slot
              name="chip"
              v-bind="slotProps"
            ></slot>
          </template>
        </KMultiSelectInput>
      </div>

      <KMultiSelectDropdown
        ref="dropdownComponent"
        :isOpen="isOpen"
        :options="dropdownOptions"
        :selectedValues="selectedValuesArray"
        :listboxId="listboxId"
        :listboxLabel="label || placeholder"
        :noResultsText="noResultsText"
        :searchText="internalSearchText"
        :itemText="itemText"
        :itemValue="itemValue"
        :indeterminateValues="indeterminateValues"
        :multiple="multiple"
        :hideSelected="hideSelected"
        :listboxMessages="computedListboxMessages"
        :expanded="expanded"
        :maxHeight="listMaxHeight"
        :disabled="disabled"
        @input="onListboxInput"
        @active-descendant-change="id => (activeDescendantId = id)"
      >
        <template
          v-if="$scopedSlots.option"
          #option="slotProps"
        >
          <!-- @slot Custom slot for rendering option items in the dropdown menu. -->
          <slot
            name="option"
            v-bind="slotProps"
          ></slot>
        </template>
        <template
          v-if="$scopedSlots['no-results']"
          #no-results
        >
          <!-- @slot Optional slot as alternative to `noResultsText` prop -->
          <slot
            name="no-results"
            :searchText="internalSearchText"
          ></slot>
        </template>
      </KMultiSelectDropdown>
    </div>

    <div class="kmselect-feedback">
      <div
        v-if="showError"
        :id="errorId"
        class="kmselect-feedback-text"
        :style="{ color: 'var(--tokens-error)' }"
      >
        <!-- @slot Optional slot as alternative to `invalidText` prop -->
        <slot name="error">
          {{ invalidText }}
        </slot>
      </div>
      <div
        v-else
        class="kmselect-feedback-text"
      >
        &nbsp;
      </div>
    </div>
  </div>

</template>


<script>

  import { ref, computed, watch, getCurrentInstance, nextTick, onBeforeUnmount } from 'vue';

  import globalThemeState from '../../../styles/globalThemeState';

  import KMultiSelectInput from './components/KMultiSelectInput.vue';
  import KMultiSelectDropdown from './components/KMultiSelectDropdown.vue';

  import useMultiSelectList from './useMultiSelectList.js';
  import useMultiSelectCascade from './useMultiSelectCascade.js';
  import useMultiSelectDropdownLogic from './useMultiSelectDropdown.js';

  let componentCount = 0;

  /**
   * A searchable select component supporting both single and multiple selections, flat
   * arrays, and deeply nested hierarchical trees with cascading selection behavior.
   */
  export default {
    name: 'KMultiSelect',

    components: {
      KMultiSelectInput,
      KMultiSelectDropdown,
    },

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const uid = componentCount++;

      if (
        process.env.NODE_ENV !== 'production' &&
        props.autoSelectParent &&
        props.autoSelectChild
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          `[KMultiSelect] 'autoSelectParent' and 'autoSelectChild' cannot be used together ` +
            `because they give a checked parent conflicting meanings. Use at most one of them.`,
        );
      }

      const internalSearchText = ref(props.searchText || '');
      const inputFocused = ref(false);
      const isHovered = ref(false);
      const containerEl = ref(null);
      const inputComponent = ref(null);
      const dropdownComponent = ref(null);
      const activeDescendantId = ref(null);

      const showError = computed(
        () => props.invalid && (Boolean(props.invalidText) || Boolean(instance.proxy.$slots.error)),
      );

      const listboxId = computed(() => `kmselect-listbox-${uid}`);
      const labelId = computed(() => `kmselect-label-${uid}`);
      const errorId = computed(() => `kmselect-error-${uid}`);
      const inputId = computed(() => `kmselect-input-${uid}`);
      const selectedValuesId = computed(() => `kmselect-selected-${uid}`);

      watch(
        () => props.searchText,
        val => {
          internalSearchText.value = val;
        },
      );

      function setSearchText(val) {
        internalSearchText.value = val;
        emit('update:searchText', val);
      }

      // suppressFilter is owned here so it can be passed to both composables.
      // The dropdown composable sets it to true when pre-filling; the list
      // composable reads it to skip filtering; onSearchInput clears it.
      const suppressFilter = ref(false);

      const {
        normalizedOptions,
        displayedOptions,
        selectedOptionsData,
        getOptionValue,
        getOptionText,
        removeOption: defaultRemoveOption,
        clearAll,
      } = useMultiSelectList(props, emit, {
        searchText: internalSearchText,
        suppressFilter,
        messages: props.messages,
      });

      const selectedValuesSummary = computed(() => {
        const count = selectedOptionsData.value.length;
        if (count === 0) return '';
        return props.messages.itemsSelected ? props.messages.itemsSelected({ count }) : '';
      });

      const { isOpen, openDropdown, closeDropdown, toggleDropdown } = useMultiSelectDropdownLogic(
        props,
        {
          containerEl,
          setSearchText,
          suppressFilter,
          searchText: internalSearchText,
          normalizedOptions,
          getOptionValue,
          getOptionText,
        },
      );

      // True only while the floating dropdown is showing. In expanded mode the list is always
      // visible, so the active border, label colour and hover key off this instead of isOpen.
      const isPopupOpen = computed(() => isOpen.value && !props.expanded);

      const {
        indeterminateValues,
        onListboxInput: onCascadeListboxInput,
        removeOption,
      } = useMultiSelectCascade(props, emit, {
        normalizedOptions,
        getOptionValue,
        setSearchText,
        closeDropdown,
        getOptionText,
        defaultRemoveOption,
        messages: props.messages,
      });
      watch(
        selectedOptionsData,
        options => {
          if (props.multiple || isPopupOpen.value) return;
          const label = options.length ? getOptionText(options[0]) : '';
          // In expanded mode the list stays visible behind the field, so the label
          // written back here must not filter it down to the single selection.
          if (props.expanded) suppressFilter.value = Boolean(label);
          setSearchText(label);
        },
        { immediate: true },
      );

      const inputRef = computed(() => inputComponent.value?.$refs?.inputEl ?? null);

      const selectedValuesArray = computed(() => {
        if (Array.isArray(props.value)) return props.value;
        return props.value !== null && props.value !== undefined ? [props.value] : [];
      });

      function onInputKeydown(event) {
        const navigationKeys = ['ArrowDown', 'ArrowUp'];

        if (navigationKeys.includes(event.key)) {
          // Without this, if the user navigates to the component without tabbing into it,
          // arrow keys do move through the dropdown options but no visual focus marker
          // is shown on the active option.
          globalThemeState.inputModality = 'keyboard';

          if (!isOpen.value) {
            event.preventDefault();
            openDropdown();
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              nextTick(() => {
                dropdownComponent.value?.forwardKeydown(event);
              });
            }
            return;
          }
          event.preventDefault();
          dropdownComponent.value?.forwardKeydown(event);
          return;
        }

        switch (event.key) {
          case 'Escape':
            // Expanded mode has no popup to dismiss, so let Escape reach the host
            // (a modal, for instance) instead of swallowing it here.
            if (props.expanded) break;
            event.preventDefault();
            event.stopPropagation();
            closeDropdown();
            inputRef.value?.focus();
            break;

          case 'Enter':
            event.preventDefault();
            event.stopPropagation();
            if (!isOpen.value) {
              openDropdown();
            } else {
              if (!dropdownComponent.value?.hasFocusedOption()) {
                dropdownComponent.value?.moveFocusByOne(1);
              }
              dropdownComponent.value?.toggleFocusedOption();

              if (dropdownOptions.value.length === 0 && internalSearchText.value) {
                setSearchText('');
              }
            }
            break;

          case 'Backspace':
            if (
              props.multiple &&
              !internalSearchText.value &&
              selectedValuesArray.value.length > 0
            ) {
              // Multi-select: remove the last chip
              event.preventDefault();
              removeOption(selectedValuesArray.value[selectedValuesArray.value.length - 1]);
            } else if (
              !props.multiple &&
              !isOpen.value &&
              !internalSearchText.value &&
              selectedValuesArray.value.length > 0
            ) {
              // Single-select: open dropdown so user can edit/change the selection.
              // Guard with !isOpen.value so holding Backspace after clearing the
              // pre-filled text does NOT re-trigger openDropdown() in a loop.
              event.preventDefault();
              openDropdown();
            }
            break;

          case 'Tab':
            closeDropdown();
            break;

          case ' ':
            // Like Backspace above: with an empty search box this is a command, not text.
            // A leading space cannot affect filtering anyway, since the query is trimmed.
            // Unlike Enter, Space has no fallback to the top match: Enter accepts, Space
            // only toggles the active option. Falling back would let a single Space press
            // select an option the user never navigated to.
            if (
              isOpen.value &&
              !internalSearchText.value &&
              dropdownComponent.value?.hasFocusedOption()
            ) {
              event.preventDefault();
              dropdownComponent.value?.toggleFocusedOption();
              break;
            }
            if (!internalSearchText.value) {
              event.preventDefault();
            }
            if (!isOpen.value) {
              openDropdown();
            }
            break;

          default:
            if (!isOpen.value && event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
              openDropdown();
            }
        }
      }

      function onSearchInput(val) {
        // Lift filter suppression when the user starts typing.
        if (suppressFilter.value) suppressFilter.value = false;
        setSearchText(val);
        if (!isOpen.value && val) openDropdown();
      }

      function onInputFocus() {
        inputFocused.value = true;
        emit('focus');
      }

      function onContainerClick(event) {
        if (props.disabled) return;
        if (dropdownComponent.value?.$el?.contains(event.target)) return;
        inputComponent.value?.focus();
        if (!isOpen.value) {
          openDropdown();
        }
      }

      watch(isOpen, newVal => {
        if (newVal && !props.expanded) {
          nextTick(() => {
            inputRef.value?.focus();
            if (!props.multiple && suppressFilter.value) {
              inputRef.value?.select();
            }
          });
        }
      });

      let blurFrameId = null;
      function onInputBlur() {
        inputFocused.value = false;
        blurFrameId = requestAnimationFrame(() => {
          if (containerEl.value && !containerEl.value.contains(document.activeElement)) {
            closeDropdown();
            if (props.expanded) {
              activeDescendantId.value = null;
              dropdownComponent.value?.clearActiveOption();
            }
            emit('blur');
          }
        });
      }

      onBeforeUnmount(() => {
        cancelAnimationFrame(blurFrameId);
      });

      async function onClearAll() {
        clearAll();
        setSearchText('');
        await nextTick();
        inputRef.value?.focus();
      }

      function onToggleDropdown() {
        inputComponent.value?.focus();
        toggleDropdown();
      }

      async function onListboxInput(newValues) {
        // In expanded mode the list stays rendered while disabled, and `pointer-events: none`
        // only stops real pointer input, not the synthetic clicks assistive tech dispatches.
        if (props.disabled) return;
        onCascadeListboxInput(newValues);
        await nextTick();

        if (props.multiple) {
          setSearchText('');
        }

        await nextTick();
        const inputEl = inputRef.value;
        if (inputEl) {
          inputEl.focus();
          const len = inputEl.value.length;
          inputEl.setSelectionRange(len, len);
        }
      }

      async function onChipRemove(val) {
        removeOption(val);
        await nextTick();
        inputRef.value?.focus();
      }

      const dropdownOptions = computed(() => displayedOptions.value);

      const computedListboxMessages = computed(() => {
        const result = {};
        const keys = [
          'clickable',
          'allOptionsSelected',
          'allOptionsDeselected',
          'optionDeselected',
          'partiallySelected',
        ];
        keys.forEach(key => {
          if (typeof props.messages[key] === 'function') {
            result[key] = props.messages[key];
          }
        });
        return Object.keys(result).length > 0 ? result : undefined;
      });

      function focus() {
        inputComponent.value?.focus();
      }

      const isLabelInline = computed(
        () =>
          Boolean(props.label) &&
          selectedOptionsData.value.length === 0 &&
          !internalSearchText.value &&
          !inputFocused.value,
      );
      const computedPlaceholder = computed(() => (isLabelInline.value ? '' : props.placeholder));

      return {
        listboxId,
        labelId,
        errorId,
        inputId,
        selectedValuesId,
        selectedValuesSummary,
        activeDescendantId,
        isOpen,
        internalSearchText,
        inputFocused,
        isHovered,
        showError,
        isPopupOpen,
        containerEl,
        inputComponent,
        dropdownComponent,
        dropdownOptions,
        computedListboxMessages,
        selectedOptionsData,
        selectedValuesArray,
        indeterminateValues,
        isLabelInline,
        computedPlaceholder,
        onContainerClick,
        onChipRemove,
        onClearAll,
        onToggleDropdown,
        onSearchInput,
        onInputFocus,
        onInputBlur,
        onListboxInput,
        onInputKeydown,
        // eslint-disable-next-line vue/no-unused-properties
        focus,
      };
    },

    props: {
      /**
       * Custom CSS styles applied to the root container element.
       */
      appearanceOverrides: {
        type: Object,
        default: null,
      },
      /**
       * The selected value. An array in multiple mode, or a string, number,
       * or `null` in single mode.
       */
      value: {
        default: null,
        validator: v =>
          v === null || Array.isArray(v) || typeof v === 'string' || typeof v === 'number',
      },
      /**
       * Array of option objects or primitive values to display in the dropdown.
       */
      // eslint-disable-next-line vue/no-unused-properties
      options: {
        type: Array,
        required: true,
      },
      /**
       * The current search input text.
       */
      searchText: {
        type: String,
        default: '',
      },
      /**
       * Whether multiple options can be selected.
       */
      multiple: {
        type: Boolean,
        default: true,
      },
      /**
       * Key on option objects used as the display label.
       */
      itemText: {
        type: String,
        default: 'label',
      },
      /**
       * Key on option objects used to identify each selection.
       */
      itemValue: {
        type: String,
        default: 'value',
      },
      /**
       * Additional option object keys to include in search matching.
       * By default, only itemText is searched.
       */
      // eslint-disable-next-line vue/no-unused-properties
      searchKeys: {
        type: Array,
        default: null,
      },
      /**
       * Label for the field.
       */
      label: {
        type: String,
        default: '',
      },
      /**
       * Placeholder text shown in the input when nothing is selected.
       */
      placeholder: {
        type: String,
        default: '',
      },
      /**
       * Text shown in the dropdown when the search query has no matching options.
       */
      noResultsText: {
        type: String,
        default: null,
      },
      /**
       * Whether or not the field is disabled.
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * Whether or not the current value is invalid.
       */
      invalid: {
        type: Boolean,
        default: false,
      },
      /**
       * Error text shown when `invalid` is true.
       */
      invalidText: {
        type: String,
        default: '',
      },
      /**
       * Shows a clear button when a value is selected.
       */
      clearable: {
        type: Boolean,
        default: false,
      },
      /**
       * Only applies to hierarchical options. When true, selecting an option
       * also selects all of the parents above it.
       */
      // eslint-disable-next-line vue/no-unused-properties
      autoSelectParent: {
        type: Boolean,
        default: false,
      },
      /**
       * Only applies to hierarchical options. When true, selecting or
       * deselecting a parent also selects or deselects everything under it.
       */
      // eslint-disable-next-line vue/no-unused-properties
      autoSelectChild: {
        type: Boolean,
        default: false,
      },
      /**
       * Only applies to hierarchical options. When true, selecting all children
       * of a parent selects the parent as well.
       */
      // eslint-disable-next-line vue/no-unused-properties
      autoPromoteParent: {
        type: Boolean,
        default: true,
      },
      /**
       * Localized functions for UI labels and screen reader announcements.
       */
      messages: {
        type: Object,
        required: true,
        validator(messages) {
          const requiredKeys = [
            'clearText',
            'open',
            'close',
            'clickable',
            'allOptionsSelected',
            'allOptionsDeselected',
            'optionDeselected',
          ];
          const missing = requiredKeys.filter(key => typeof messages[key] !== 'function');
          if (missing.length) {
            // eslint-disable-next-line no-console
            console.warn(
              `[KMultiSelect] 'messages' prop keys must be functions. Missing or invalid keys: ${missing.join(', ')}`,
            );
            return false;
          }
          return true;
        },
      },
      /**
       * Hides already-selected options from the dropdown. Only supported for flat lists,
       * not nested options.
       */
      hideSelected: {
        type: Boolean,
        default: false,
      },
      /**
       * Renders the listbox inline and permanently visible below the field instead of as a
       * floating dropdown: no open/close lifecycle, and no toggle button.
       */
      expanded: {
        type: Boolean,
        default: false,
      },
      /**
       * Max height of the option list before it scrolls internally, as a CSS length.
       * Applies to both the floating dropdown and the expanded list.
       */
      listMaxHeight: {
        type: String,
        default: '256px',
      },
    },
  };

</script>


<style lang="scss" scoped>

  .kmselect {
    position: relative;
    width: 100%;
    max-width: 100%;
  }

  .kmselect-field {
    position: relative;
    width: 100%;
  }

  .kmselect-container {
    position: relative;
    width: 100%;

    &.has-label {
      padding-top: 20px;
    }
  }

  .kmselect-label {
    position: absolute;
    top: 2px;
    left: 10px;
    z-index: 1;
    display: table;
    width: fit-content;
    margin-bottom: 0;
    font-size: 15px;
    font-weight: normal;
    transition:
      color 0.1s ease,
      transform 0.2s ease;
    transform-origin: left;

    &.is-inline {
      cursor: text;
      transform: translateY(24px) scale(1.1);
    }

    &.is-floating {
      transform: translateY(0) scale(1);
    }
  }

  .kmselect-feedback {
    margin: 4px 0 0;
    font-size: 12px;
  }

</style>
